import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { entryDate, monthSummaries, totalBalance } from "@/lib/financial/calc";
import type { FinancialAccount, FinancialCategory, FinancialEntry, Period } from "@/lib/financial/types";
import { filterLedger, isEntryLinked, moneyTone, thClass, type LedgerKind } from "./helpers";
import { cn } from "@/lib/utils";
import type { FormMode } from "./FinancialFormDialog";
import type { PeriodState } from "./PeriodPicker";
import { Panel, PanelTitle, PillFilter } from "./ui";
import { useFinancialFormat } from "./useFinancialFormat";


interface Props {
  entries: FinancialEntry[];
  accounts: FinancialAccount[];
  categories: FinancialCategory[];
  period: Period;
  onPeriod: (p: PeriodState) => void;
  today: string;
  canCreate: boolean;
  canUpdate: boolean;
  onForm: (mode: FormMode) => void;
}

export function LedgerTab({ entries, accounts, categories, period, onPeriod, today, canCreate, canUpdate, onForm }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const [kind, setKind] = useState<LedgerKind>("all");
  const catById = new Map(categories.map((c) => [c.id, c]));
  const accById = new Map(accounts.map((a) => [a.id, a]));

  const labelOf = (e: FinancialEntry) =>
    `${e.description} ${e.detail ?? ""} ${fmt.category(catById.get(e.category_id ?? ""))} ${fmt.account(accById.get(e.account_id))}`;

  const inPeriod = filterLedger(entries, period, "all", "", labelOf);
  const rows = filterLedger(entries, period, kind, "", labelOf);
  const inflow = inPeriod.filter((e) => Number(e.amount) > 0).reduce((a, e) => a + Number(e.amount), 0);
  const outflow = inPeriod.filter((e) => Number(e.amount) < 0).reduce((a, e) => a + Number(e.amount), 0);

  const year = Number(today.slice(0, 4));
  const months = monthSummaries(accounts, entries, year, Number(today.slice(5, 7)) - 1);
  const balance = totalBalance(accounts, entries);

  const filterOptions = [
    { value: "all" as const, label: t("ledger.filterAll") },
    { value: "in" as const, label: t("ledger.filterIn") },
    { value: "out" as const, label: t("ledger.filterOut") },
    { value: "manual" as const, label: t("ledger.filterManual") },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Panel className="p-5">
        <PanelTitle
          title={t("ledger.monthsTitle")}
          subtitle={t("ledger.monthsSub")}
          right={<span className="text-sm text-muted-foreground">{t("ledger.balanceToday")} <strong className="text-foreground">{fmt.brl0(balance)}</strong></span>}
        />
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6 xl:grid-cols-12">
          {months.map((m) => {
            const selected = period.from <= m.to && period.to >= m.from;
            return (
              <button
                key={m.month}
                type="button"
                aria-pressed={selected}
                onClick={() => onPeriod({ from: m.from, to: m.to, presetKey: null })}
                className={cn(
                  "rounded-xl border px-2.5 py-3 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30",
                )}
              >
                <div className={cn("text-[11px] font-semibold uppercase tracking-wider", selected ? "text-primary" : "text-muted-foreground")}>{fmt.monthShort(m.month)}</div>
                <div className={cn("mt-1.5 font-display text-[15px] font-extrabold tracking-tight", m.closing < 0 ? "text-state-danger-foreground" : "text-foreground")}>{fmt.num(m.closing)}</div>
                <div className="text-[11px] text-muted-foreground">{t("ledger.endedWith")}</div>
                <div className="mt-2 flex flex-col gap-0.5 border-t border-border/70 pt-1.5 text-[11px] tabular-nums">
                  <span className="text-state-success-foreground">+ {fmt.num(m.inflow)}</span>
                  <span className="text-state-danger-foreground">− {fmt.num(Math.abs(m.outflow))}</span>
                </div>
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-lg font-bold">{t("ledger.listTitle")}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{t("ledger.listSub")}</p>
          </div>
          <div className="flex justify-end flex-wrap items-center gap-2">
            {canCreate ? (
              <Button size="sm" onClick={() => onForm({ type: "entry" })}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                {t("header.newEntry")}
              </Button>
            ) : null}
            <PillFilter options={filterOptions} value={kind} onChange={(v) => setKind(v as LedgerKind)} label={t("ledger.listTitle")} />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-7 gap-y-3 border-b border-border bg-muted/40 px-5 py-4">
          <div><div className="text-xs text-muted-foreground">{t("ledger.in")}</div><div className="mt-0.5 font-display text-lg font-extrabold text-state-success-foreground">{fmt.brl(inflow)}</div></div>
          <div><div className="text-xs text-muted-foreground">{t("ledger.out")}</div><div className="mt-0.5 font-display text-lg font-extrabold text-state-danger-foreground">{fmt.brl(Math.abs(outflow))}</div></div>
          <div><div className="text-xs text-muted-foreground">{t("ledger.net")}</div><div className={cn("mt-0.5 font-display text-lg font-extrabold", moneyTone(inflow + outflow))}>{fmt.brl(inflow + outflow)}</div></div>
          <div className="ml-auto self-end text-sm text-muted-foreground">{t("ledger.count", { count: rows.length })}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr>
                <th className={cn(thClass, "w-32 pl-5")}>{t("ledger.date")}</th>
                <th className={thClass}>{t("ledger.what")}</th>
                <th className={cn(thClass, "w-52")}>{t("ledger.category")}</th>
                <th className={cn(thClass, "w-48")}>{t("ledger.account")}</th>
                <th className={cn(thClass, "w-28")}>{t("ledger.origin")}</th>
                <th className={cn(thClass, "w-32 text-right")}>{t("ledger.amount")}</th>
                <th className={cn(thClass, "w-14 pr-5")}><span className="sr-only">{t("ledger.fix")}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const d = entryDate(e);
                return (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="py-2.5 pl-5 pr-3 align-middle">
                      <div className="font-semibold tabular-nums">{fmt.dnum(d)}</div>
                      <div className="text-xs text-muted-foreground">{fmt.dow(d)}</div>
                    </td>
                    <td className="px-3 py-2.5 align-middle">
                      <div className="font-medium">{e.description || fmt.category(catById.get(e.category_id ?? ""))}</div>
                      <div className="text-xs text-muted-foreground">{e.detail ?? (e.origin === "manual" ? t("ledger.manualSub") : "")}</div>
                    </td>
                    <td className="px-3 py-2.5 align-middle text-foreground/80">{fmt.category(catById.get(e.category_id ?? ""))}</td>
                    <td className="px-3 py-2.5 align-middle text-foreground/80">{fmt.account(accById.get(e.account_id))}</td>
                    <td className="px-3 py-2.5 align-middle">
                      <span className={cn("whitespace-nowrap rounded-full px-2.5 py-1 text-xs text-foreground/80", e.origin === "manual" ? "bg-state-warning-soft" : "bg-muted")}>
                        {t(`ledger.origins.${e.origin}`)}
                      </span>
                    </td>
                    <td className={cn("px-3 py-2.5 text-right align-middle font-display text-sm font-bold tabular-nums", moneyTone(Number(e.amount)))}>{fmt.signed(Number(e.amount))}</td>
                    <td className="py-2.5 pl-2 pr-5 text-right align-middle">
                      {/* baixas de título se corrigem desfazendo a baixa em A receber / A pagar */}
                      {canUpdate && !isEntryLinked(e) ? (
                        <Button size="icon-sm" variant="outline" aria-label={t("ledger.fix")} onClick={() => onForm({ type: "editEntry", entry: e })}>
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!rows.length ? <p className="px-5 py-7 text-center text-sm text-muted-foreground">{t("ledger.empty")}</p> : null}
      </Panel>
    </div>
  );
}
